import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  addLeadInterestApi,
  promoteLeadInterestApi,
  removeLeadInterestApi,
} from "@/lib/api";
import { useCatalogNoun } from "@/hooks/useCatalogNoun";
import type { CatalogNoun } from "@/lib/brand";

/**
 * Mutations over a lead's programs of interest. There is no query hook: the
 * list arrives inside the lead-detail payload.
 *
 * Every one of these can move `leads.product_id` — adding the first interest
 * promotes it, promoting moves it, and removing the primary hands it to a
 * successor. That is the same "structural" change `useUpdateLead` refetches
 * for, so the board grouping and the product facet must be invalidated too.
 * These do not go through `useUpdateLead`, so they cannot inherit its
 * `onSettled` and have to say it themselves.
 */
function useInterestMutation<TArgs>(
  leadId: string,
  fn: (args: TArgs) => Promise<void>,
  successMessage: (noun: CatalogNoun) => string,
) {
  const queryClient = useQueryClient();
  // The client's word ("programa" / "producto"), so a toast never names the
  // other industry's thing.
  const noun = useCatalogNoun();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      void queryClient.invalidateQueries({ queryKey: ["pipeline-stage"] });
      void queryClient.invalidateQueries({ queryKey: ["product-counts"] });
      toast.success(successMessage(noun));
    },
    onError: (err: Error) => {
      toast.error(err.message || `No se pudo actualizar los ${noun.plural} de interés`);
    },
  });
}

export function useAddLeadInterest(leadId: string) {
  return useInterestMutation<string>(
    leadId,
    (productId) => addLeadInterestApi(leadId, productId),
    (n) => `${n.Singular} agregado`,
  );
}

export function usePromoteLeadInterest(leadId: string) {
  return useInterestMutation<string>(
    leadId,
    (interestId) => promoteLeadInterestApi(leadId, interestId),
    (n) => `${n.Singular} principal actualizado`,
  );
}

export function useRemoveLeadInterest(leadId: string) {
  return useInterestMutation<string>(
    leadId,
    (interestId) => removeLeadInterestApi(leadId, interestId),
    (n) => `${n.Singular} eliminado`,
  );
}
